import type { JSX } from 'react';
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Upload,
  RefreshCw,
  KeyRound,
  Wand2,
  FileText,
  AlertTriangle,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import {
  KB_DOCUMENT_EXTENSIONS,
  type KnowledgeBase,
  type KbDocument,
  type FtsLanguage,
} from '@echo-work-platform/shared-types';
import { api, deleteErrorMessage } from '@/lib/api-client';
import { formatNumber } from '@/lib/utils';
import { Select } from '@/components/shared/select';

const FTS_OPTIONS = [
  { value: 'hungarian', label: 'Magyar' },
  { value: 'english', label: 'Angol' },
  { value: 'simple', label: 'Egyszerű (nyelvfüggetlen)' },
];

// Poll while anything is still being indexed.
const isBusy = (status: string): boolean => status === 'pending' || status === 'indexing';

const KB_ENDPOINT = '/api/admin/knowledge-bases';

interface SettingsResponse {
  settings: { embeddingProvider: string | null; toolDescProvider: string | null };
  frozen: boolean;
}

const STATUS_STYLE: Record<string, string> = {
  ready: 'bg-emerald-100 text-emerald-700',
  indexed: 'bg-emerald-100 text-emerald-700',
  indexing: 'bg-blue-100 text-blue-700',
  pending: 'bg-slate-100 text-slate-600',
  failed: 'bg-rose-100 text-rose-700',
};

function StatusBadge({ status }: { status: string }): JSX.Element {
  return (
    <span
      className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status] ?? STATUS_STYLE.pending}`}
    >
      {status}
    </span>
  );
}

export function KnowledgeBases(): JSX.Element {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ftsLanguage, setFtsLanguage] = useState<FtsLanguage>('hungarian');
  const [managed, setManaged] = useState<KnowledgeBase | null>(null);

  const settings = useQuery({
    queryKey: ['/api/admin/system-settings'],
    queryFn: () => api.get<SettingsResponse>('/api/admin/system-settings'),
  });
  const configured = Boolean(settings.data?.settings.embeddingProvider);

  const list = useQuery({
    queryKey: [KB_ENDPOINT],
    queryFn: () => api.get<KnowledgeBase[]>(KB_ENDPOINT),
    // Poll while any KB is still indexing so the status/chunk count updates live.
    refetchInterval: (q) => (q.state.data?.some((k) => isBusy(k.status)) ? 2500 : false),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<KnowledgeBase>(KB_ENDPOINT, { name, description: description || null, ftsLanguage }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KB_ENDPOINT] });
      setCreating(false);
      setName('');
      setDescription('');
      setFtsLanguage('hungarian');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${KB_ENDPOINT}/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KB_ENDPOINT] }),
    onError: (error: unknown) => alert(deleteErrorMessage(error)),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-heading">Knowledge Bases</h2>
        <button className="btn-primary" disabled={!configured} onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> Új
        </button>
      </div>

      {!settings.isLoading && !configured && (
        <div className="card flex items-start gap-3 border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Tudásbázis létrehozásához előbb be kell állítani a beágyazási modellt a{' '}
            <Link to="/settings" className="font-semibold underline">
              Beállítások
            </Link>{' '}
            oldalon.
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Név</th>
              <th className="px-4 py-2.5">Állapot</th>
              <th className="px-4 py-2.5">Chunkok</th>
              <th className="px-4 py-2.5">Utolsó indexelés</th>
              <th className="w-32 px-4 py-2.5">Műveletek</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                  Betöltés…
                </td>
              </tr>
            )}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                  Nincs adat.
                </td>
              </tr>
            )}
            {list.data?.map((kb) => (
              <tr key={kb.id} className="hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <div className="font-medium text-heading">{kb.name}</div>
                  {kb.description && <div className="text-xs text-slate-500">{kb.description}</div>}
                </td>
                <td className="px-4 py-2.5">
                  <StatusBadge status={kb.status} />
                </td>
                <td className="px-4 py-2.5">{formatNumber(kb.chunkCount)}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">
                  {kb.lastIndexedAt ? new Date(kb.lastIndexedAt).toLocaleString('hu-HU') : '—'}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    <button className="btn-secondary px-2 py-1 text-xs" onClick={() => setManaged(kb)}>
                      Kezelés
                    </button>
                    <button
                      title="Töröl"
                      className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      onClick={() => {
                        if (confirm(`Törlés: "${kb.name}"? Ez törli a dokumentumokat és chunkokat is.`))
                          remove.mutate(kb.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <Modal title="Új tudásbázis" onClose={() => setCreating(false)}>
          <div className="space-y-4">
            <div>
              <label className="label">Név</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="label">Leírás</label>
              <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div>
              <label className="label">Keresési nyelv (full-text)</label>
              <Select
                value={ftsLanguage}
                onChange={(v) => setFtsLanguage(v as FtsLanguage)}
                options={FTS_OPTIONS}
              />
              <p className="mt-1 text-xs text-slate-500">
                A dokumentumok nyelve a szótövezéshez. Angol tartalomhoz válaszd az „Angol"-t.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setCreating(false)}>
                Mégse
              </button>
              <button
                className="btn-primary"
                disabled={!name || create.isPending}
                onClick={() => create.mutate()}
              >
                Létrehozás
              </button>
            </div>
            {create.isError && <p className="text-sm text-rose-600">{String(create.error)}</p>}
          </div>
        </Modal>
      )}

      {managed && (
        <Modal title={`Kezelés — ${managed.name}`} onClose={() => setManaged(null)} wide>
          <KbManage kb={managed} />
        </Modal>
      )}
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--overlay)' }}
    >
      <div className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto p-6`}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-heading">{title}</h3>
          <button className="rounded p-1 text-slate-400 hover:bg-slate-100" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function KbManage({ kb }: { kb: KnowledgeBase }): JSX.Element {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [toolDesc, setToolDesc] = useState(kb.mcpToolDescription ?? '');
  const docsKey = [`${KB_ENDPOINT}/${kb.id}/documents`];

  const docs = useQuery({
    queryKey: docsKey,
    queryFn: () => api.get<KbDocument[]>(`${KB_ENDPOINT}/${kb.id}/documents`),
    // Poll while any document is still being processed.
    refetchInterval: (q) => (q.state.data?.some((d) => isBusy(d.status)) ? 2000 : false),
  });

  // Live KB row (shares the parent list's polling) for up-to-date status / chunk count.
  const kbList = useQuery({
    queryKey: [KB_ENDPOINT],
    queryFn: () => api.get<KnowledgeBase[]>(KB_ENDPOINT),
  });
  const liveKb = kbList.data?.find((k) => k.id === kb.id) ?? kb;

  const settings = useQuery({
    queryKey: ['/api/admin/system-settings'],
    queryFn: () => api.get<SettingsResponse>('/api/admin/system-settings'),
  });
  const toolDescConfigured = Boolean(settings.data?.settings.toolDescProvider);
  const canAutoGenerate = toolDescConfigured && liveKb.status === 'ready';

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: docsKey });
    void queryClient.invalidateQueries({ queryKey: [KB_ENDPOINT] });
  };

  const upload = useMutation({
    mutationFn: (files: File[]) => {
      const fd = new FormData();
      files.forEach((f) => fd.append('file', f));
      return api.upload<{ created: unknown[]; skipped: { filename: string; reason: string }[] }>(
        `${KB_ENDPOINT}/${kb.id}/documents`,
        fd,
      );
    },
    onSuccess: invalidateAll,
  });
  const deleteDoc = useMutation({
    mutationFn: (docId: string) => api.delete(`${KB_ENDPOINT}/${kb.id}/documents/${docId}`),
    onSuccess: invalidateAll,
  });
  const reindex = useMutation({
    mutationFn: () => api.post(`${KB_ENDPOINT}/${kb.id}/reindex`, {}),
    onSuccess: invalidateAll,
  });
  const rotateKey = useMutation({
    mutationFn: () => api.post(`${KB_ENDPOINT}/${kb.id}/rotate-key`, {}),
  });
  const saveDesc = useMutation({
    mutationFn: () => api.patch(`${KB_ENDPOINT}/${kb.id}/tool-description`, { description: toolDesc }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KB_ENDPOINT] }),
  });
  const regenDesc = useMutation({
    mutationFn: () =>
      api.post<{ description: string | null }>(`${KB_ENDPOINT}/${kb.id}/regenerate-description`, {}),
    onSuccess: (res) => {
      if (res?.description) setToolDesc(res.description);
      void queryClient.invalidateQueries({ queryKey: [KB_ENDPOINT] });
    },
  });

  return (
    <div className="space-y-6">
      {/* Documents */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="font-semibold text-heading">Dokumentumok</h4>
          <div className="flex gap-2">
            <button
              className="btn-secondary px-2 py-1 text-xs"
              disabled={reindex.isPending}
              onClick={() => reindex.mutate()}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Újraindexelés
            </button>
            <button
              className="btn-primary px-2 py-1 text-xs"
              disabled={upload.isPending}
              onClick={() => fileRef.current?.click()}
            >
              {upload.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              {upload.isPending ? 'Feltöltés…' : 'Feltöltés (több is)'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept={KB_DOCUMENT_EXTENSIONS.join(',')}
              multiple
              className="hidden"
              onChange={(e) => {
                // Snapshot the File objects synchronously — resetting value clears the FileList,
                // which would empty it before the async upload reads it.
                const arr = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (arr.length) upload.mutate(arr);
              }}
            />
          </div>
        </div>
        {upload.isError && <p className="mb-2 text-sm text-rose-600">{String(upload.error)}</p>}
        {upload.data?.skipped && upload.data.skipped.length > 0 && (
          <p className="mb-2 text-xs text-amber-700">
            Kihagyva: {upload.data.skipped.map((s) => `${s.filename} (${s.reason})`).join('; ')}
          </p>
        )}
        {docs.data && docs.data.length > 0 && docs.data.some((d) => isBusy(d.status)) && (
          <p className="mb-2 flex items-center gap-1.5 text-xs text-blue-600">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Indexelés folyamatban… {docs.data.filter((d) => d.status === 'indexed').length}/{docs.data.length}{' '}
            kész
          </p>
        )}
        <div className="card divide-y divide-slate-100">
          {docs.data?.length === 0 && (
            <p className="p-3 text-sm text-slate-500">Nincs feltöltött dokumentum.</p>
          )}
          {docs.data?.map((d) => (
            <div key={d.id} className="flex items-center justify-between p-3 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                {isBusy(d.status) ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-500" />
                ) : d.status === 'indexed' ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                ) : (
                  <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                )}
                <span className="truncate">{d.filename}</span>
                <StatusBadge status={d.status} />
                {d.status === 'indexed' && (
                  <span className="shrink-0 text-xs text-slate-500">{d.chunkCount} chunk</span>
                )}
                {d.status === 'failed' && d.errorMessage && (
                  <span className="truncate text-xs text-rose-600">{d.errorMessage}</span>
                )}
              </div>
              <button
                className="shrink-0 rounded p-1 text-rose-500 hover:bg-rose-50"
                onClick={() => deleteDoc.mutate(d.id)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Tool description */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="font-semibold text-heading">
            MCP tool-leírás{' '}
            <span className="text-xs font-normal text-slate-500">({liveKb.mcpToolDescriptionSource})</span>
          </h4>
          <button
            className="btn-secondary px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
            disabled={regenDesc.isPending || !canAutoGenerate}
            title={!canAutoGenerate ? 'Automatikus generálás jelenleg nem elérhető' : undefined}
            onClick={() => regenDesc.mutate()}
          >
            {regenDesc.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Wand2 className="h-3.5 w-3.5" />
            )}
            {regenDesc.isPending ? 'Generálás…' : 'Újragenerálás'}
          </button>
        </div>
        {!toolDescConfigured && (
          <p className="mb-2 flex items-center gap-1.5 text-xs text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            Automatikus generáláshoz állíts be tool-leírás modellt a{' '}
            <Link to="/settings" className="font-medium underline">
              Beállításokban
            </Link>
            . Addig csak kézi bevitel.
          </p>
        )}
        {toolDescConfigured && liveKb.status !== 'ready' && (
          <p className="mb-2 flex items-center gap-1.5 text-xs text-slate-500">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            Automatikus generálás az indexelés után érhető el (állapot: ready). Jelenleg: {liveKb.status}.
          </p>
        )}
        <textarea
          className="input h-28 text-xs"
          value={toolDesc}
          onChange={(e) => setToolDesc(e.target.value)}
          placeholder="A modell ez alapján dönti el, mikor hívja ezt a tudásbázist…"
        />
        {regenDesc.isError && <p className="mt-1 text-xs text-rose-600">{String(regenDesc.error)}</p>}
        <div className="mt-2 flex justify-end">
          <button
            className="btn-primary px-2 py-1 text-xs"
            disabled={saveDesc.isPending}
            onClick={() => saveDesc.mutate()}
          >
            Mentés (kézi felülírás)
          </button>
        </div>
      </section>

      {/* Key rotation */}
      <section className="flex items-center justify-between border-t border-slate-100 pt-4">
        <div className="text-sm text-slate-500">MCP hozzáférési kulcs</div>
        <button
          className="btn-secondary px-2 py-1 text-xs"
          disabled={rotateKey.isPending}
          onClick={() => rotateKey.mutate()}
        >
          <KeyRound className="h-3.5 w-3.5" /> Kulcs rotálása
        </button>
      </section>
      {rotateKey.isSuccess && (
        <p className="text-xs text-emerald-600">
          Kulcs rotálva — a desktop a következő frissítéskor átveszi.
        </p>
      )}
    </div>
  );
}
